// Live spoken-word counter helpers for the Script Editor.
//
// Only the *spoken* column of each format counts — this feeds a read-aloud
// runtime estimate, so anything not said on camera (scene #, directions, notes,
// A/B-roll, VFX/SFX, props, gestures, qual/reviewer notes) is excluded.
//
// Markdown is stripped with the SAME stripper the CSV export uses
// (lib/scriptExport.js) rather than a second copy. Link URLs are stored
// out-of-band in row._links (not inline in the cell text), so they never reach
// this counter — only what a writer actually typed into the spoken cell is
// counted. A word is one whitespace-delimited token (the accepted convention
// for read-aloud estimates).

import { stripMarkdown } from '../lib/scriptExport';

// Format -> the single column whose contents a person says aloud on camera.
// Mockumentary: `line` is the character's spoken line (same principle as Skit).
// Recipe: `recipe` is the narrated recipe content; `gestures` are physical
// actions, not spoken. Formats absent here (None / Riff) have nothing to count
// and the counter is not rendered for them.
export const SPOKEN_COL = {
  Skit:         'line',
  TH:           'narration',
  AI:           'narration',
  Mockumentary: 'line',
  Longform:     'line',   // D-089: spoken text = Line, same principle as Skit
  Recipe:       'recipe',
};

// Word count for one raw spoken-cell string. A "word" is a whitespace-delimited
// token that contains at least one letter or number — so standalone punctuation
// tokens (an ellipsis "…"/"...", a lone dash "—", "?", etc.) are NOT counted.
// Punctuation attached to a real word ("wait...") still counts as that one word.
export function countCellWords(raw) {
  if (!raw) return 0;
  const stripped = stripMarkdown(raw).trim();
  if (!stripped) return 0;
  return stripped.split(/\s+/).filter(t => /[\p{L}\p{N}]/u.test(t)).length;
}

// seconds = round(words / wpm * 60), formatted:
//   < 60s        -> "47s"
//   60s–59:59    -> "13:47"
//   >= 1 hour    -> "1:02:09"
export function formatRuntime(wordCount, wpm) {
  if (!wpm || wpm <= 0) return '0s';
  const total = Math.round((wordCount / wpm) * 60);
  if (total < 60) return `${total}s`;
  const pad = n => String(n).padStart(2, '0');
  const hrs  = Math.floor(total / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  return hrs > 0 ? `${hrs}:${pad(mins)}:${pad(secs)}` : `${mins}:${pad(secs)}`;
}

// Thousands-separated word count ("1,240").
export function formatWordCount(n) {
  return n.toLocaleString('en-US');
}

// WPM personal-preference constants.
export const WPM_STORAGE_KEY = 'hf-wpm';
export const WPM_DEFAULT = 303;
export const WPM_MIN = 30;
export const WPM_MAX = 400;

export function clampWpm(n) {
  return Math.min(WPM_MAX, Math.max(WPM_MIN, n));
}
